/**
 * Platform detection helpers.
 *
 * The web build and the Capacitor iOS build share the exact same bundle, so
 * every native code path must be guarded at runtime. Import from here rather
 * than reaching into `@capacitor/core` directly so the checks stay uniform.
 */

import { Capacitor } from '@capacitor/core';

/** True when running inside a native Capacitor shell (iOS/Android). */
export function isNativePlatform(): boolean {
  return Capacitor.isNativePlatform();
}

/** True specifically on iOS (WKWebView). */
export function isIOS(): boolean {
  return Capacitor.getPlatform() === 'ios';
}

/** 'ios' | 'android' | 'web' */
export function getPlatform(): string {
  return Capacitor.getPlatform();
}

/**
 * The canonical public origin for the app, used for building links that must
 * resolve in a normal browser (invite links, OAuth/email redirects) even when
 * generated from inside the native shell — where `window.location.origin` is a
 * non-routable scheme like `capacitor://localhost`.
 *
 * Reads VITE_PUBLIC_APP_URL; falls back to the live origin on web.
 */
export function getPublicAppUrl(): string {
  const configured = import.meta.env.VITE_PUBLIC_APP_URL as string | undefined;
  if (configured) return configured.replace(/\/$/, '');
  if (!isNativePlatform() && typeof window !== 'undefined') {
    return window.location.origin;
  }
  // Last-resort default so native builds still produce shareable https links.
  return 'https://app.projexhq.com';
}
